import React, { useState } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServicesPreview = () => {
  const [activeService, setActiveService] = useState('velocity');

  const services = [
    {
      id: 'velocity',
      title: 'Velocity Engineering',
      subtitle: 'Transform Sprint Plans Into Production Reality',
      description: `Move beyond agile ceremonies to create true engineering velocity. We embed with your teams to eliminate blockers, optimize workflows, and build ownership culture that delivers consistent results.`,
      capabilities: [
        'Sprint velocity optimization and flow analysis',
        'Technical debt elimination strategies',
        'Continuous delivery pipeline enhancement',
        'Team ownership culture development'
      ],
      outcomes: [
        '3x improvement in delivery velocity within 90 days',
        '90% reduction in sprint scope creep',
        '50% decrease in production incidents',
        'Measurable increase in team satisfaction scores'
      ],
      icon: 'Zap',
      color: 'from-blue-600 to-blue-800',
      accentColor: 'blue-500'
    },
    {
      id: 'ownership',
      title: 'Ownership Culture Transformation',
      subtitle: 'Build Teams That Own Their Success',
      description: `Replace process-heavy methodologies with ownership-driven culture. Our embedded approach transforms how teams think about accountability, quality, and continuous improvement.`,
      capabilities: [
        'Ownership mindset development programs',
        'Quality-first engineering practices',
        'Cross-functional collaboration enhancement',
        'Continuous improvement culture building'
      ],
      outcomes: [
        '85% improvement in code quality metrics',
        '70% reduction in cross-team dependencies',
        '95% team satisfaction with ownership model',
        'Sustainable culture change that scales'
      ],
      icon: 'Users',
      color: 'from-green-600 to-green-800',
      accentColor: 'green-500'
    },
    {
      id: 'architecture',
      title: 'Architectural Modernization',
      subtitle: 'Build Systems That Scale and Evolve',
      description: `Transform legacy architectures into modern, scalable systems without disrupting operations. Our approach balances innovation with stability to create platforms that enable rapid development.`,
      capabilities: [
        'Legacy system modernization strategies',
        'Microservices architecture design',
        'Cloud-native platform development',
        'Developer experience optimization'
      ],
      outcomes: [
        '60% improvement in system performance',
        '80% reduction in deployment complexity',
        '90% increase in developer productivity',
        'Future-ready architecture that adapts'
      ],
      icon: 'Layers',
      color: 'from-purple-600 to-purple-800',
      accentColor: 'purple-500'
    }
  ];

  const activeServiceData = services.find(service => service.id === activeService);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            Three Core Service Areas
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Specialized expertise designed to transform different aspects of
            your engineering organization
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Service Tabs */}
          <div className="lg:col-span-1">
            <div className="space-y-4">
              {services.map((service) => (
                <motion.button
                  key={service.id}
                  onClick={() => setActiveService(service.id)}
                  className={`w-full text-left p-6 rounded-xl border transition-all duration-300 ${
                    activeService === service.id
                      ? "border-accent bg-accent/5 elevation-subtle"
                      : "border-border hover:border-accent/30 hover:bg-muted/50"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-start space-x-4">
                    <div
                      className={`w-12 h-12 rounded-lg bg-gradient-to-br ${service.color} flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon
                        name={service.icon}
                        size={20}
                        className="text-white"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-primary mb-1">
                        {service.title}
                      </h3>
                      <p className="text-sm text-text-secondary">
                        {service.subtitle}
                      </p>
                    </div>
                    <Icon
                      name="ChevronRight"
                      size={20}
                      className={`text-text-secondary transition-transform duration-300 ${
                        activeService === service.id
                          ? "rotate-90 text-accent"
                          : ""
                      }`}
                    />
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Service Details */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-muted rounded-2xl p-8"
              >
                <div className="mb-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div
                      className={`w-16 h-16 rounded-xl bg-gradient-to-br ${activeServiceData.color} flex items-center justify-center`}
                    >
                      <Icon
                        name={activeServiceData.icon}
                        size={28}
                        className="text-white"
                      />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-primary">
                        {activeServiceData.title}
                      </h3>
                      <p className="text-text-secondary">
                        {activeServiceData.subtitle}
                      </p>
                    </div>
                  </div>
                  <p className="text-text-secondary leading-relaxed">
                    {activeServiceData.description}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Capabilities */}
                  <div>
                    <h4 className="text-lg font-semibold text-primary mb-4 flex items-center">
                      <Icon
                        name="Settings"
                        size={20}
                        className="mr-2 text-accent"
                      />
                      Core Capabilities
                    </h4>
                    <ul className="space-y-3">
                      {activeServiceData.capabilities.map(
                        (capability, index) => (
                          <li
                            key={index}
                            className="flex items-start text-sm text-text-secondary"
                          >
                            <Icon
                              name="Check"
                              size={16}
                              className="mr-3 mt-0.5 text-success flex-shrink-0"
                            />
                            {capability}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>

                  {/* Expected Outcomes */}
                  <div>
                    <h4 className="text-lg font-semibold text-primary mb-4 flex items-center">
                      <Icon
                        name="Target"
                        size={20}
                        className="mr-2 text-accent"
                      />
                      Expected Outcomes
                    </h4>
                    <ul className="space-y-3">
                      {activeServiceData.outcomes.map((outcome, index) => (
                        <li
                          key={index}
                          className="flex items-start text-sm text-text-secondary"
                        >
                          <Icon
                            name="TrendingUp"
                            size={16}
                            className="mr-3 mt-0.5 text-success flex-shrink-0"
                          />
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-border">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      variant="default"
                      className="bg-cta hover:bg-cta/90 text-cta-foreground"
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      Start Assessment
                    </Button>
                    <Button
                      variant="outline"
                      iconName="FileText"
                      iconPosition="left"
                    >
                      Download Case Study
                    </Button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;
